import { createContext, useContext } from "react";
import { Document, PicoExtraction, Project } from "@/types/models";
import type { MentionProvider } from "@lumanity/ui/chat";

interface ProjectDetailContextValue {
  projectId: string;
  project: Project;
  projectDocuments: Document[];
  picoExtractions: PicoExtraction[];
  setPicoExtractions: (extractions: PicoExtraction[]) => void;
  activeStage: number;
  handleStageComplete: (stageIndex: number) => void;
  /** Shows another workflow step by its key (e.g. "Evidence Gathering"), like clicking it in
   * the stepper. Only changes the step being viewed — never the project's status. */
  goToStep: (stepKey: string) => void;
  continueLabel: string;
  pageTitle: string;
  templateStatuses: Record<string, string>;
  setTemplateStatuses: (statuses: Record<string, string>) => void;
  /** Narrows the assistant widget's RAG scope to specific documents for the
   * current step (e.g. the doc open in QA's validation modal, or a report
   * section's selected sources). Null falls back to all project documents. */
  setChatDocumentIds: (documentIds: string[] | null) => void;
  /** Plain-text summary of the current step's state (e.g. extraction table
   * completeness, outline refinement progress, report drafting progress —
   * whichever step is mounted), merged into the assistant's system prompt.
   * Null when the step has no such state. */
  setStepStateNote: (note: string | null) => void;
  /** The current step's @-mention provider (e.g. report sections, project
   * documents), passed through to the assistant widget. Null when the step
   * has no mentionable items. */
  setMentionProvider: (provider: MentionProvider | null) => void;
}

export const ProjectDetailContext = createContext<ProjectDetailContextValue | null>(null);

export function useProjectDetailContext() {
  const ctx = useContext(ProjectDetailContext);
  if (!ctx) throw new Error("useProjectDetailContext must be used within a ProjectDetailProvider");
  return ctx;
}

export const ProjectDetailProvider = ProjectDetailContext.Provider;
